import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import {
  Package, AlertTriangle, CheckCircle2, XCircle,
  ArrowDownCircle, ArrowUpCircle, History, TrendingDown,
  Search, Warehouse, Building2, User, ChevronDown, ChevronRight,
} from "lucide-react";
import { Link } from "wouter";

interface StockEntry {
  inventoryId: number | null;
  ownerType: string;
  ownerUserId: number | null;
  ownerName?: string;
  currentStock: number;
  reorderPoint: number;
  status: 'ok' | 'low' | 'critical';
  updatedAt: string | null;
}

interface InventoryItem {
  id: number;
  name: string;
  category: string;
  unitOfMeasure: string;
  costPerUnit: string;
  typicalOwner: string;
  orgStock: StockEntry;
  pmStocks: StockEntry[];
  totalStock: number;
  overallStatus: 'ok' | 'low' | 'critical';
  inventoryId: number | null;
  currentStock: number;
  reorderPoint: number;
  status: 'ok' | 'low' | 'critical';
  updatedAt: string | null;
}

interface SupplyMovement {
  id: number;
  supplyCatalogId: number;
  ownerType: string | null;
  ownerUserId: number | null;
  ownerName: string;
  type: string;
  quantity: string;
  reference: string | null;
  cleaningJobId: number | null;
  notes: string | null;
  performedBy: number | null;
  createdAt: string;
}

interface PmClient {
  id: number;
  name: string;
  email: string;
}

export default function AdminInventory() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("stock");
  const [searchQuery, setSearchQuery] = useState("");
  const [restockOpen, setRestockOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [selectedStockEntry, setSelectedStockEntry] = useState<StockEntry | null>(null);
  const [restockForm, setRestockForm] = useState({
    quantity: "", totalCost: "", supplier: "", notes: "",
    ownerType: "org", ownerUserId: "",
  });
  const [adjustForm, setAdjustForm] = useState({ newStock: "", notes: "", ownerType: "org", ownerUserId: "" });
  const [reorderForm, setReorderForm] = useState({ reorderPoint: "" });
  const [reorderOpen, setReorderOpen] = useState(false);
  const [movementFilter, setMovementFilter] = useState("all");
  const [movementItemFilter, setMovementItemFilter] = useState("all");
  const [movementDateFrom, setMovementDateFrom] = useState("");
  const [movementDateTo, setMovementDateTo] = useState("");
  const [expandedPmSections, setExpandedPmSections] = useState<Set<string>>(new Set());

  const { data: inventory = [], isLoading } = useQuery<InventoryItem[]>({
    queryKey: ["/api/admin/inventory"],
  });

  const { data: movements = [] } = useQuery<SupplyMovement[]>({
    queryKey: ["/api/admin/inventory/movements"],
  });

  const { data: lowStock } = useQuery<{ lowStockCount: number; items: any[] }>({
    queryKey: ["/api/admin/inventory/low-stock"],
  });

  const { data: pmClients = [] } = useQuery<PmClient[]>({
    queryKey: ["/api/admin/inventory/pm-clients"],
  });

  const restockMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/inventory/restock", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory/movements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory/low-stock"] });
      setRestockOpen(false);
      setRestockForm({ quantity: "", totalCost: "", supplier: "", notes: "", ownerType: "org", ownerUserId: "" });
      toast({ title: "Stock replenished successfully" });
    },
    onError: (e: any) => toast({ title: "Failed to restock", description: e.message, variant: "destructive" }),
  });

  const adjustMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/inventory/adjust", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory/movements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory/low-stock"] });
      setAdjustOpen(false);
      setAdjustForm({ newStock: "", notes: "", ownerType: "org", ownerUserId: "" });
      toast({ title: "Stock adjusted" });
    },
    onError: (e: any) => toast({ title: "Failed to adjust", description: e.message, variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/inventory/reorder-point", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/inventory/low-stock"] });
      setReorderOpen(false);
      setReorderForm({ reorderPoint: "" });
      toast({ title: "Reorder point updated" });
    },
    onError: (e: any) => toast({ title: "Failed to update", description: e.message, variant: "destructive" }),
  });

  const openRestock = (item: InventoryItem, forEntry?: StockEntry) => {
    setSelectedItem(item);
    setSelectedStockEntry(forEntry || null);
    setRestockForm({
      quantity: "",
      totalCost: "",
      supplier: "",
      notes: "",
      ownerType: forEntry?.ownerType || (item.typicalOwner === 'mixed' ? 'org' : item.typicalOwner) || "org",
      ownerUserId: forEntry?.ownerUserId?.toString() || "",
    });
    setRestockOpen(true);
  };

  const openAdjust = (item: InventoryItem, entry: StockEntry) => {
    setSelectedItem(item);
    setSelectedStockEntry(entry);
    setAdjustForm({
      newStock: entry.currentStock.toString(),
      notes: "",
      ownerType: entry.ownerType,
      ownerUserId: entry.ownerUserId?.toString() || "",
    });
    setAdjustOpen(true);
  };

  const handleRestock = () => {
    if (!selectedItem) return;
    restockMutation.mutate({
      supplyCatalogId: selectedItem.id,
      quantity: restockForm.quantity,
      totalCost: restockForm.totalCost || "0",
      supplier: restockForm.supplier || null,
      notes: restockForm.notes || null,
      ownerType: restockForm.ownerType,
      ownerUserId: restockForm.ownerType === 'pm_client' && restockForm.ownerUserId ? parseInt(restockForm.ownerUserId) : null,
    });
  };

  const handleAdjust = () => {
    if (!selectedItem) return;
    adjustMutation.mutate({
      supplyCatalogId: selectedItem.id,
      newStock: adjustForm.newStock,
      notes: adjustForm.notes || null,
      ownerType: adjustForm.ownerType,
      ownerUserId: adjustForm.ownerType === 'pm_client' && adjustForm.ownerUserId ? parseInt(adjustForm.ownerUserId) : null,
    });
  };

  const handleReorderPoint = () => {
    if (!selectedItem) return;
    reorderMutation.mutate({
      supplyCatalogId: selectedItem.id,
      reorderPoint: parseFloat(reorderForm.reorderPoint),
    });
  };

  const togglePmSection = (pmKey: string) => {
    setExpandedPmSections(prev => {
      const next = new Set(prev);
      next.has(pmKey) ? next.delete(pmKey) : next.add(pmKey);
      return next;
    });
  };

  const filtered = inventory.filter(item => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.category.toLowerCase().includes(q);
    }
    return true;
  });

  const totalItems = inventory.length;
  const lowStockCount = lowStock?.lowStockCount || 0;
  const criticalCount = inventory.filter(i => i.overallStatus === 'critical').length;
  const okCount = inventory.filter(i => i.overallStatus === 'ok').length;

  const filteredMovements = movements.filter(m => {
    if (movementFilter !== 'all' && m.type !== movementFilter) return false;
    if (movementItemFilter !== 'all' && m.supplyCatalogId !== parseInt(movementItemFilter)) return false;
    if (movementDateFrom && m.createdAt && new Date(m.createdAt) < new Date(movementDateFrom)) return false;
    if (movementDateTo && m.createdAt && new Date(m.createdAt) > new Date(movementDateTo + 'T23:59:59')) return false;
    return true;
  });

  // Gather unique PMs that have stock
  const pmWithStock = new Map<number, string>();
  for (const item of inventory) {
    for (const ps of item.pmStocks) {
      if (ps.ownerUserId && ps.ownerName) {
        pmWithStock.set(ps.ownerUserId, ps.ownerName);
      }
    }
  }

  const statusBadge = (status: string) => {
    if (status === 'ok') return <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-[10px]" data-testid="badge-status-ok"><CheckCircle2 className="h-3 w-3 mr-1" /> In Stock</Badge>;
    if (status === 'low') return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 text-[10px]" data-testid="badge-status-low"><AlertTriangle className="h-3 w-3 mr-1" /> Low</Badge>;
    return <Badge className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 text-[10px]" data-testid="badge-status-critical"><XCircle className="h-3 w-3 mr-1" /> Out of Stock</Badge>;
  };

  const ownerBadge = (ownerType: string | null, ownerName?: string) => {
    if (ownerType === 'pm_client') {
      return (
        <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 dark:text-blue-300 gap-1">
          <User className="h-2.5 w-2.5" /> {ownerName || 'PM Client'}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 dark:text-amber-300 gap-1">
        <Building2 className="h-2.5 w-2.5" /> Service Org
      </Badge>
    );
  };

  const movementIcon = (type: string) => {
    if (type === 'consumption') return <ArrowDownCircle className="h-4 w-4 text-red-500" />;
    if (type === 'restock') return <ArrowUpCircle className="h-4 w-4 text-green-500" />;
    return <History className="h-4 w-4 text-blue-500" />;
  };

  const getItemName = (catalogId: number) => {
    return inventory.find(i => i.id === catalogId)?.name || `Item #${catalogId}`;
  };

  const renderStockRow = (item: InventoryItem, entry: StockEntry, label?: string) => (
    <tr key={`${item.id}-${entry.ownerType}-${entry.ownerUserId}`} className="border-b last:border-0 hover:bg-muted/50">
      <td className="py-3 pl-4">
        {label && <span className="text-xs text-muted-foreground">{label}</span>}
      </td>
      <td className="py-3 text-center">
        <span className={`text-lg font-bold ${entry.status === 'critical' ? 'text-red-600' : entry.status === 'low' ? 'text-yellow-600' : ''}`}>
          {entry.currentStock}
        </span>
      </td>
      <td className="py-3 text-center text-muted-foreground">{entry.reorderPoint}</td>
      <td className="py-3 text-center">{statusBadge(entry.status)}</td>
      <td className="py-3 text-right pr-2">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="outline"
            size="sm"
            className="text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-950 text-xs h-7"
            onClick={() => openRestock(item, entry)}
            data-testid={`btn-restock-${item.id}-${entry.ownerType}-${entry.ownerUserId}`}
          >
            <ArrowUpCircle className="h-3 w-3 mr-1" /> Restock
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={() => openAdjust(item, entry)}
            data-testid={`btn-adjust-${item.id}-${entry.ownerType}-${entry.ownerUserId}`}
          >
            Adjust
          </Button>
        </div>
      </td>
    </tr>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="inventory-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-display font-bold flex items-center gap-2" data-testid="inventory-title">
              <Warehouse className="h-6 w-6 text-primary" />
              Cleaning Inventory
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Track stock levels by owner — org-purchased and PM client–supplied inventory</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Items</p>
                <p className="text-xl font-bold" data-testid="stat-total-items">{totalItems}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">In Stock</p>
                <p className="text-xl font-bold" data-testid="stat-ok-items">{okCount}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Low Stock</p>
                <p className="text-xl font-bold" data-testid="stat-low-items">{lowStockCount}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Out of Stock</p>
                <p className="text-xl font-bold" data-testid="stat-critical-items">{criticalCount}</p>
              </div>
            </div>
          </CardContent></Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="stock" className="gap-1.5" data-testid="tab-stock">
              <Package className="h-4 w-4" />
              Stock Levels
            </TabsTrigger>
            <TabsTrigger value="movements" className="gap-1.5" data-testid="tab-movements">
              <History className="h-4 w-4" />
              Movement History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stock" className="space-y-4 mt-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search supplies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-inventory"
                />
              </div>
            </div>

            {inventory.length === 0 && !isLoading ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Warehouse className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg">No inventory items</h3>
                  <p className="text-sm text-muted-foreground mt-1 mb-4 text-center max-w-sm">
                    Add supplies in the <Link href="/admin/unit-costs" className="text-primary underline">Supply Catalog</Link> first, then manage stock here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* ORG STOCK SECTION */}
                <Card>
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <div className="h-6 w-6 rounded bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                        <Building2 className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
                      </div>
                      Service Org Stock
                      <Badge variant="secondary" className="text-[10px] ml-auto">Org-purchased</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="table-org-inventory">
                        <thead>
                          <tr className="border-b text-left bg-muted/30">
                            <th className="pb-2 pt-2 pl-4 font-medium text-muted-foreground">Supply</th>
                            <th className="pb-2 pt-2 font-medium text-muted-foreground text-center">Org Stock</th>
                            <th className="pb-2 pt-2 font-medium text-muted-foreground text-center">Reorder At</th>
                            <th className="pb-2 pt-2 font-medium text-muted-foreground text-center">Status</th>
                            <th className="pb-2 pt-2 font-medium text-muted-foreground text-right pr-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map(item => (
                            <tr key={item.id} className="border-b last:border-0 hover:bg-muted/50" data-testid={`inventory-row-${item.id}`}>
                              <td className="py-3 pl-4">
                                <div className="font-medium">{item.name}</div>
                                <div className="text-xs text-muted-foreground">{item.category} · {item.unitOfMeasure} · ${parseFloat(item.costPerUnit).toFixed(2)}/unit</div>
                                {item.pmStocks.length > 0 && (
                                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                    +{item.pmStocks.reduce((s, p) => s + p.currentStock, 0)} PM-supplied
                                  </div>
                                )}
                              </td>
                              <td className="py-3 text-center">
                                <span className={`text-lg font-bold ${item.orgStock.status === 'critical' ? 'text-red-600' : item.orgStock.status === 'low' ? 'text-yellow-600' : ''}`}>
                                  {item.orgStock.currentStock}
                                </span>
                              </td>
                              <td className="py-3 text-center text-muted-foreground">
                                <button
                                  className="hover:text-primary cursor-pointer underline-offset-2 hover:underline"
                                  onClick={() => { setSelectedItem(item); setReorderForm({ reorderPoint: item.orgStock.reorderPoint.toString() }); setReorderOpen(true); }}
                                  data-testid={`btn-reorder-${item.id}`}
                                >
                                  {item.orgStock.reorderPoint}
                                </button>
                              </td>
                              <td className="py-3 text-center">{statusBadge(item.orgStock.status)}</td>
                              <td className="py-3 text-right pr-2">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-950 text-xs h-7"
                                    onClick={() => openRestock(item, item.orgStock)}
                                    data-testid={`btn-restock-${item.id}`}
                                  >
                                    <ArrowUpCircle className="h-3 w-3 mr-1" /> Restock
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={() => openAdjust(item, item.orgStock)}
                                    data-testid={`btn-adjust-${item.id}`}
                                  >
                                    Adjust
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* PM CLIENT STOCK SECTIONS */}
                {pmWithStock.size > 0 && Array.from(pmWithStock.entries()).map(([pmUserId, pmName]) => {
                  const pmKey = `pm-${pmUserId}`;
                  const isExpanded = expandedPmSections.has(pmKey);
                  const pmItems = filtered.filter(item => item.pmStocks.some(ps => ps.ownerUserId === pmUserId));
                  if (pmItems.length === 0) return null;
                  const pmLow = pmItems.filter(item => {
                    const ps = item.pmStocks.find(p => p.ownerUserId === pmUserId);
                    return ps && (ps.status === 'low' || ps.status === 'critical');
                  }).length;
                  return (
                    <Collapsible key={pmKey} open={isExpanded} onOpenChange={() => togglePmSection(pmKey)}>
                      <Card>
                        <CollapsibleTrigger asChild>
                          <CardHeader className="pb-2 pt-4 px-4 cursor-pointer hover:bg-muted/30 rounded-t-lg transition-colors">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <div className="h-6 w-6 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                <User className="h-3.5 w-3.5 text-blue-700 dark:text-blue-300" />
                              </div>
                              {pmName} — Client Stock
                              <Badge variant="secondary" className="text-[10px]">PM-supplied</Badge>
                              {pmLow > 0 && <Badge className="text-[10px] bg-yellow-100 text-yellow-700">{pmLow} low</Badge>}
                              <div className="ml-auto">
                                {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                              </div>
                            </CardTitle>
                          </CardHeader>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <CardContent className="p-0">
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm" data-testid={`table-pm-inventory-${pmUserId}`}>
                                <thead>
                                  <tr className="border-b text-left bg-muted/30">
                                    <th className="pb-2 pt-2 pl-4 font-medium text-muted-foreground">Supply</th>
                                    <th className="pb-2 pt-2 font-medium text-muted-foreground text-center">PM Stock</th>
                                    <th className="pb-2 pt-2 font-medium text-muted-foreground text-center">Reorder At</th>
                                    <th className="pb-2 pt-2 font-medium text-muted-foreground text-center">Status</th>
                                    <th className="pb-2 pt-2 font-medium text-muted-foreground text-right pr-2">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pmItems.map(item => {
                                    const ps = item.pmStocks.find(p => p.ownerUserId === pmUserId)!;
                                    return (
                                      <tr key={item.id} className="border-b last:border-0 hover:bg-muted/50" data-testid={`pm-inventory-row-${item.id}-${pmUserId}`}>
                                        <td className="py-3 pl-4">
                                          <div className="font-medium">{item.name}</div>
                                          <div className="text-xs text-muted-foreground">{item.category} · {item.unitOfMeasure}</div>
                                        </td>
                                        <td className="py-3 text-center">
                                          <span className={`text-lg font-bold ${ps.status === 'critical' ? 'text-red-600' : ps.status === 'low' ? 'text-yellow-600' : ''}`}>
                                            {ps.currentStock}
                                          </span>
                                        </td>
                                        <td className="py-3 text-center text-muted-foreground">{ps.reorderPoint}</td>
                                        <td className="py-3 text-center">{statusBadge(ps.status)}</td>
                                        <td className="py-3 text-right pr-2">
                                          <div className="flex items-center justify-end gap-1">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-950 text-xs h-7"
                                              onClick={() => openRestock(item, ps)}
                                              data-testid={`btn-restock-pm-${item.id}-${pmUserId}`}
                                            >
                                              <ArrowUpCircle className="h-3 w-3 mr-1" /> Restock
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="text-xs h-7"
                                              onClick={() => openAdjust(item, ps)}
                                              data-testid={`btn-adjust-pm-${item.id}-${pmUserId}`}
                                            >
                                              Adjust
                                            </Button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="movements" className="space-y-4 mt-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={movementFilter} onValueChange={setMovementFilter}>
                <SelectTrigger className="w-40" data-testid="select-movement-filter">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="consumption">Consumption</SelectItem>
                  <SelectItem value="restock">Restock</SelectItem>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                </SelectContent>
              </Select>
              <Select value={movementItemFilter} onValueChange={setMovementItemFilter}>
                <SelectTrigger className="w-44" data-testid="select-movement-item-filter">
                  <SelectValue placeholder="All items" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  {inventory.map(item => (
                    <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={movementDateFrom}
                onChange={(e) => setMovementDateFrom(e.target.value)}
                className="w-36"
                data-testid="input-date-from"
              />
              <Input
                type="date"
                value={movementDateTo}
                onChange={(e) => setMovementDateTo(e.target.value)}
                className="w-36"
                data-testid="input-date-to"
              />
            </div>

            {filteredMovements.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <History className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">No movement history yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredMovements.map(m => {
                  const qty = parseFloat(m.quantity);
                  return (
                    <Card key={m.id} data-testid={`movement-${m.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          {movementIcon(m.type)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm">{getItemName(m.supplyCatalogId)}</span>
                              <Badge variant="outline" className="text-[10px]">{m.type}</Badge>
                              {ownerBadge(m.ownerType, m.ownerName)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {m.notes || m.reference || '—'}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className={`font-bold text-sm ${qty > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {qty > 0 ? '+' : ''}{qty}
                            </span>
                            <div className="text-[10px] text-muted-foreground">
                              {m.createdAt ? new Date(m.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" }) : '—'}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Restock Dialog */}
      <Dialog open={restockOpen} onOpenChange={setRestockOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restock: {selectedItem?.name}</DialogTitle>
            <DialogDescription>Log a purchase to replenish inventory</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Purchased by *</label>
              <Select
                value={restockForm.ownerType}
                onValueChange={(v) => setRestockForm(f => ({ ...f, ownerType: v, ownerUserId: v === 'org' ? '' : f.ownerUserId }))}
              >
                <SelectTrigger data-testid="select-restock-owner-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="org">Service Org</SelectItem>
                  <SelectItem value="pm_client">PM Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {restockForm.ownerType === 'pm_client' && (
              <div>
                <label className="text-sm font-medium">PM Client *</label>
                <Select
                  value={restockForm.ownerUserId}
                  onValueChange={(v) => setRestockForm(f => ({ ...f, ownerUserId: v }))}
                >
                  <SelectTrigger data-testid="select-restock-pm-client">
                    <SelectValue placeholder="Select PM client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {pmClients.map(pm => (
                      <SelectItem key={pm.id} value={String(pm.id)}>{pm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Quantity *</label>
              <Input
                type="number"
                min="1"
                value={restockForm.quantity}
                onChange={(e) => setRestockForm(f => ({ ...f, quantity: e.target.value }))}
                placeholder="Enter quantity"
                data-testid="input-restock-qty"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Total Cost ($)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={restockForm.totalCost}
                onChange={(e) => setRestockForm(f => ({ ...f, totalCost: e.target.value }))}
                placeholder="0.00"
                data-testid="input-restock-cost"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Supplier</label>
              <Input
                value={restockForm.supplier}
                onChange={(e) => setRestockForm(f => ({ ...f, supplier: e.target.value }))}
                placeholder="e.g. Amazon, Costco"
                data-testid="input-restock-supplier"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={restockForm.notes}
                onChange={(e) => setRestockForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes"
                rows={2}
                data-testid="input-restock-notes"
              />
            </div>
            <Button
              className="w-full"
              onClick={handleRestock}
              disabled={!restockForm.quantity || (restockForm.ownerType === 'pm_client' && !restockForm.ownerUserId) || restockMutation.isPending}
              data-testid="btn-submit-restock"
            >
              {restockMutation.isPending ? "Saving..." : "Log Purchase & Restock"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust Dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Stock: {selectedItem?.name}</DialogTitle>
            <DialogDescription>
              Adjusting {adjustForm.ownerType === 'pm_client' ? (pmClients.find(p => String(p.id) === adjustForm.ownerUserId)?.name || 'PM Client') : 'Service Org'} stock (current: {selectedStockEntry?.currentStock})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">New Stock Level *</label>
              <Input
                type="number"
                min="0"
                value={adjustForm.newStock}
                onChange={(e) => setAdjustForm(f => ({ ...f, newStock: e.target.value }))}
                data-testid="input-adjust-stock"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reason / Notes</label>
              <Textarea
                value={adjustForm.notes}
                onChange={(e) => setAdjustForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Why is stock being adjusted?"
                rows={2}
                data-testid="input-adjust-notes"
              />
            </div>
            <Button
              className="w-full"
              onClick={handleAdjust}
              disabled={adjustForm.newStock === '' || adjustMutation.isPending}
              data-testid="btn-submit-adjust"
            >
              {adjustMutation.isPending ? "Saving..." : "Update Stock"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reorder Point Dialog */}
      <Dialog open={reorderOpen} onOpenChange={setReorderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reorder Point: {selectedItem?.name}</DialogTitle>
            <DialogDescription>Set the minimum org stock threshold before alerts are triggered</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Reorder Point *</label>
              <Input
                type="number"
                min="0"
                value={reorderForm.reorderPoint}
                onChange={(e) => setReorderForm({ reorderPoint: e.target.value })}
                data-testid="input-reorder-point"
              />
            </div>
            <Button
              className="w-full"
              onClick={handleReorderPoint}
              disabled={!reorderForm.reorderPoint || reorderMutation.isPending}
              data-testid="btn-submit-reorder"
            >
              {reorderMutation.isPending ? "Saving..." : "Update Reorder Point"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
